import type { SiteId } from '@goodlifeplus/themes'
import resolveConfig from 'tailwindcss/resolveConfig'
import type { DefaultColors } from 'tailwindcss/types/generated/colors.js'
import type { DefaultTheme } from 'tailwindcss/types/generated/default-theme.js'
import { type Ref, onBeforeUnmount, onMounted, ref } from 'vue'

// @ts-expect-error No typing necessary
import tailwindConfig from '../../tailwind.config.js'

const SITE_ID = import.meta.env.GLP_SITE_ID

type TailwindConfig = {
  breakpoints: {
    xs: number
    sm: number
    md: number
    lg: number
    xl: number
    '2xl': number
  }
  colors: DefaultColors
  padding: DefaultTheme['spacing']
}

export function useTailwind(): TailwindConfig & {
  isXs: Ref<boolean>
  isSm: Ref<boolean>
  isMd: Ref<boolean>
  isLg: Ref<boolean>
  isXl: Ref<boolean>
  is2xl: Ref<boolean>
} {
  const fullConfig = resolveConfig(tailwindConfig(SITE_ID as SiteId))

  const rawScreens = fullConfig.theme.screens

  const breakpoints = {
    xs: rawScreens.xs ? Number(`${rawScreens.xs}`.replace('px', '')) : 0,
    sm: Number(`${rawScreens.sm}`.replace('px', '')),
    md: Number(`${rawScreens.md}`.replace('px', '')),
    lg: Number(`${rawScreens.lg}`.replace('px', '')),
    xl: Number(`${rawScreens.xl}`.replace('px', '')),
    '2xl': rawScreens['2xl']
      ? Number(`${rawScreens['2xl']}`.replace('px', ''))
      : 1536,
  }

  const isXs = ref(false)
  const isSm = ref(false)
  const isMd = ref(false)
  const isLg = ref(false)
  const isXl = ref(false)
  const is2xl = ref(false)

  const update = () => {
    if (typeof window === 'undefined') {
      return
    }

    isXs.value = window.innerWidth < breakpoints.sm
    isSm.value = window.matchMedia(`(min-width: ${breakpoints.sm}px)`).matches
    isMd.value = window.matchMedia(`(min-width: ${breakpoints.md}px)`).matches
    isLg.value = window.matchMedia(`(min-width: ${breakpoints.lg}px)`).matches
    isXl.value = window.matchMedia(`(min-width: ${breakpoints.xl}px)`).matches
    is2xl.value = window.matchMedia(
      `(min-width: ${breakpoints['2xl']}px)`
    ).matches
  }

  onMounted(() => {
    if (typeof window !== 'undefined') {
      update()
      window.addEventListener('resize', update)
    }
  })

  onBeforeUnmount(() => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', update)
    }
  })

  return {
    breakpoints,
    colors: fullConfig.theme.colors as DefaultColors,
    padding: fullConfig.theme.padding,
    isXs,
    isSm,
    isMd,
    isLg,
    isXl,
    is2xl,
  }
}
